import cookieService from '../services/cookieService';

const LIVESESSION_COOKIE = 'livesession';
const LIVESESSION_MAX_AGE_MS = 35 * 60 * 1000; // 35 minutes
const CLINIC_SYNCED_KEY = 'mcpClinicSynced';

/**
 * Raw value of the MCP `livesession` cookie (a millisecond timestamp string).
 * MCP resets this to a new value whenever it re-establishes the session, so the
 * value acts as an identifier for the current MCP session.
 *
 * @returns {string|undefined}
 */
const getLivesessionValue = () =>
  cookieService.getCookie(LIVESESSION_COOKIE);

/**
 * True when the MCP `livesession` cookie exists and is still within its
 * 35-minute freshness window (i.e. the MCP session is established).
 *
 * @returns {boolean}
 */
export const isMcpSessionLive = () => {
  const livesessionValue = getLivesessionValue();
  if (!livesessionValue) return false;
  const timestamp = parseInt(livesessionValue, 10);
  return (
    !Number.isNaN(timestamp) &&
    Date.now() - timestamp <= LIVESESSION_MAX_AGE_MS
  );
};

/**
 * Reads the stored clinic-sync record, or null if absent/unparseable.
 *
 * @returns {{ cochlearId: string, livesession: string } | null}
 */
const readClinicSyncRecord = () => {
  const raw = sessionStorage.getItem(CLINIC_SYNCED_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

/**
 * Records that MCP has switched its active clinic to the given clinic. Written
 * by the home-page Programming File Requests pod once MCP confirms the switch
 * (CHANGE_ACTIVE_CLINIC_DPX), so other MCP-iframe pages can tell the clinic is
 * synced for this browser session.
 *
 * The current `livesession` value is stored alongside the clinic id so the
 * record is scoped to the MCP session it was made under; if MCP later
 * re-establishes the session (new `livesession`), the record no longer matches
 * and the clinic is treated as unsynced.
 *
 * @param {string|number} cochlearId - The active clinic's cochlearId.
 */
export const setClinicSynced = (cochlearId) => {
  const livesessionValue = getLivesessionValue();
  if (cochlearId && livesessionValue) {
    sessionStorage.setItem(
      CLINIC_SYNCED_KEY,
      JSON.stringify({
        cochlearId: String(cochlearId),
        livesession: livesessionValue,
      }),
    );
  }
};

/**
 * Clears the recorded MCP clinic sync. Called on logout so a subsequent login
 * (which starts a fresh MCP session) does not incorrectly assume the clinic is
 * still synced.
 */
export const clearClinicSynced = () => {
  sessionStorage.removeItem(CLINIC_SYNCED_KEY);
};

/**
 * True when the given clinic has been synced to MCP under the current MCP
 * session. Both the clinic id and the `livesession` value must match, so a
 * re-established session (or a different clinic) is correctly treated as
 * unsynced.
 *
 * @param {string|number} cochlearId - The active clinic's cochlearId.
 * @returns {boolean}
 */
export const isClinicSynced = (cochlearId) => {
  if (!cochlearId) return false;
  const record = readClinicSyncRecord();
  return (
    !!record &&
    record.cochlearId === String(cochlearId) &&
    record.livesession === getLivesessionValue()
  );
};

/**
 * MCP-iframe pages assume MCP already has the correct active clinic, which only
 * gets synced by the home-page pod. Returns true when the session is live but
 * the clinic has not been synced yet - i.e. the caller should redirect the user
 * home so the sync can run before rendering the MCP iframe.
 *
 * @param {string|number} cochlearId - The active clinic's cochlearId.
 * @returns {boolean}
 */
export const shouldRedirectForClinicSync = (cochlearId) =>
  isMcpSessionLive() && !isClinicSynced(cochlearId);
